import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../core/Icon';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

const tones: Record<AlertTone, { bg: string; bd: string; fg: string; icon: string }> = {
    info: { bg: 'var(--info-soft)', bd: '#CFE2FF', fg: 'var(--info-strong)', icon: 'info' },
    success: { bg: 'var(--success-soft)', bd: '#C8ECD6', fg: 'var(--success-strong)', icon: 'circle-check' },
    warning: { bg: 'var(--warning-soft)', bd: '#F3E0B4', fg: 'var(--warning-strong)', icon: 'triangle-alert' },
    danger: { bg: 'var(--danger-soft)', bd: '#F7CDD3', fg: 'var(--danger-strong)', icon: 'circle-alert' },
    neutral: { bg: 'var(--surface-secondary)', bd: 'var(--border)', fg: 'var(--neutral-700)', icon: 'info' },
};

export interface AlertProps {
    tone?: AlertTone;
    title?: ReactNode;
    children?: ReactNode;
    action?: ReactNode;
    onDismiss?: () => void;
    /** `false` só para aviso que bloqueia uma decisão em curso. */
    dismissible?: boolean;
    style?: CSSProperties;
}

/**
 * Aviso do sistema. O "x" de fechar aparece SEMPRE: aviso que não se fecha vira
 * ruído permanente na tela, e a pessoa aprende a ignorar a faixa inteira —
 * inclusive quando ela passa a dizer algo que importa.
 *
 * `dismissible={false}` só para aviso que bloqueia uma decisão em curso, onde
 * fechar esconderia o motivo do bloqueio.
 */
export function Alert({ tone = 'info', title, children, action, onDismiss, dismissible = true, style }: AlertProps) {
    const t = tones[tone] ?? tones.info;
    const [fechado, setFechado] = useState(false);
    if (fechado) return null;

    const fechar = () => {
        setFechado(true);
        onDismiss?.();
    };

    return (
        <div role="status" style={{ display: 'flex', gap: 11, padding: '12px 14px', background: t.bg, border: `1px solid ${t.bd}`, borderRadius: 'var(--radius-md)', ...style }}>
            <Icon name={t.icon} size={17} style={{ color: t.fg, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                {title && <p style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</p>}
                {children && <div style={{ marginTop: title ? 3 : 0, fontSize: 13, lineHeight: '19px', color: 'var(--neutral-700)' }}>{children}</div>}
                {action && <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>{action}</div>}
            </div>
            {dismissible && (
                <button
                    type="button" onClick={fechar}
                    aria-label={typeof title === 'string' ? `Fechar aviso: ${title}` : 'Fechar aviso'}
                    style={{ border: 0, background: 'transparent', cursor: 'pointer', color: t.fg, opacity: 0.7, padding: 0, height: 18, display: 'flex', flex: '0 0 auto' }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.7'; }}
                >
                    <Icon name="x" size={15} />
                </button>
            )}
        </div>
    );
}
